import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { baseurl } from "../Baseurl";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default function ExportData() {
  const orgId = localStorage.getItem("organization_id");
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);

  // Filters
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const dashboardRef = useRef();

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

useEffect(() => {
  const fetchData = async () => {
    try {
      const [normalRes, strategicRes] = await Promise.all([
        axios.get(`${baseurl}getProjectTaskDetailsByOrg/${orgId}`),
        axios.get(`${baseurl}getProjectTaskDetailsByOrgstretegic/${orgId}`),
      ]);

      let normalTasks = Array.isArray(normalRes.data) ? normalRes.data : [];
      let strategicTasks = Array.isArray(strategicRes.data) ? strategicRes.data : [];

      let allTasks = [...normalTasks, ...strategicTasks];

      // 🔹 Update status for overdue tasks based on timeframe
      const now = new Date();

      allTasks = allTasks.map((task) => {
        if (
          (task.status === "todo" || task.status === "in-progress") &&
          task.timeframe // ensure timeframe exists
        ) {
          const createdDate = new Date(task.created_at);
          let isOverdue = false;

          switch (task.timeframe.toLowerCase()) {
            case "daily":
              isOverdue = now > new Date(createdDate.getTime() + 1 * 24 * 60 * 60 * 1000);
              break;
            case "weekly":
              isOverdue = now > new Date(createdDate.getTime() + 7 * 24 * 60 * 60 * 1000);
              break;
            case "monthly":
              const monthEnd = new Date(createdDate);
              monthEnd.setMonth(monthEnd.getMonth() + 1);
              isOverdue = now > monthEnd;
              break;
            case "yearly":
              const yearEnd = new Date(createdDate);
              yearEnd.setFullYear(yearEnd.getFullYear() + 1);
              isOverdue = now > yearEnd;
              break;
            default:
              break;
          }

          if (isOverdue) {
            return { ...task, status: "overdue" };
          }
        }
        return task;
      });

      setTasks(allTasks);
    } catch (err) {
      console.error("❌ Error fetching tasks:", err);
    }
  };

  if (orgId) fetchData();
}, [orgId]);

  // 🔹 Apply filters
  useEffect(() => {
    let data = [...tasks];

    if (selectedMonth) {
      data = data.filter((task) => {
        const date = new Date(task.created_at);
        const monthYear = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;
        return monthYear === selectedMonth;
      });
    }

    if (selectedStatus) {
      data = data.filter((task) => task.status === selectedStatus);
    }

    setFilteredTasks(data);
  }, [selectedMonth, selectedStatus, tasks]);

  // 🔹 Metrics
  const totalTasks = filteredTasks.length;
  const completedTasks = filteredTasks.filter(
    (t) => t.status === "complete" || t.status === "done"
  ).length;
  const incompleteTasks = filteredTasks.filter(
    (t) => t.status === "todo" || t.status === "in-progress"
  ).length;
  const overdueTasks = filteredTasks.filter((t) => t.status === "overdue").length;

  const completionData = [
    { name: "Completed", value: completedTasks },
    { name: "Incomplete", value: incompleteTasks },
    { name: "Overdue", value: overdueTasks },
  ];

  const priorityData = [
    { name: "High", value: filteredTasks.filter((t) => t.priority === "high").length },
    { name: "Medium", value: filteredTasks.filter((t) => t.priority === "medium").length },
    { name: "Low", value: filteredTasks.filter((t) => t.priority === "low").length },
  ];

  // 🔹 Export PDF
  const exportPDF = async () => {
    const input = dashboardRef.current;
    if (!input) return;

    try {
      const canvas = await html2canvas(input, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const pageHeight = 290;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 10;

      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("Task_Report.pdf");
    } catch (error) {
      console.error("❌ Error exporting PDF:", error);
    }
  };

  // 🔹 Get unique month options from tasks
  const monthOptions = [
    ...new Set(
      tasks.map((task) => {
        const date = new Date(task.created_at);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      })
    ),
  ];

  // 🔹 Unique status options
  const statusOptions = [...new Set(tasks.map((t) => t.status))];

  return (
    <div className="container mt-4" ref={dashboardRef}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold">📊 Task Reporting Dashboard</h3>
        <button className="btn btn-danger btn-sm" onClick={exportPDF}>
          ⬇️ Export PDF
        </button>
      </div>

      {/* 🔹 Filters */}
      <div className="row mb-4">
        <div className="col-md-4">
  <label className="form-label">Filter by Month</label>
  <select
    className="form-control"
    value={selectedMonth}
    onChange={(e) => setSelectedMonth(e.target.value)}
  >
    <option value="">All Months</option>
    {monthOptions.map((m, i) => {
      const [year, month] = m.split("-");
      const date = new Date(year, month - 1); // month is 0-based
      const label = date.toLocaleString("en-US", {
        month: "short",
        year: "numeric",
      }); // 👉 "Sep 2025"
      return (
        <option key={i} value={m}>
          {label}
        </option>
      );
    })}
  </select>
</div>


        <div className="col-md-4">
          <label className="form-label">Filter by Status</label>
          <select
            className="form-control"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">All Status</option>
            {statusOptions.map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 🔹 Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card shadow-sm p-3 text-center">
            <h5>Total Tasks</h5>
            <h3>{totalTasks}</h3>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm p-3 text-center">
            <h5>Completed</h5>
            <h3>{completedTasks}</h3>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm p-3 text-center">
            <h5>Incomplete</h5>
            <h3>{incompleteTasks}</h3>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm p-3 text-center">
            <h5>Overdue</h5>
            <h3>{overdueTasks}</h3>
          </div>
        </div>
      </div>

      {/* 🔹 Charts */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card shadow-sm p-3">
            <h5 className="text-center">Task Completion Chart</h5>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={completionData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label
                >
                  {completionData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card shadow-sm p-3">
            <h5 className="text-center">Task Priority Chart</h5>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={priorityData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* 🔹 Task List */}
      <div className="card shadow-sm p-3 mt-4">
        <h5 className="mb-3">📋 Task List</h5>
        <div className="table-responsive">
          <table className="table table-striped table-bordered">
            <thead className="table-dark">
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Status</th>
                <th>Timeframe</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.length > 0 ? (
                filteredTasks.map((task) => (
                  <tr key={task.id}>
                    <td>{task.id}</td>
                    <td>{task.title}</td>
                    <td>
                      <span
                        className={`badge ${
                          task.status === "complete" || task.status === "done"
                            ? "bg-success"
                            : task.status === "overdue"
                            ? "bg-danger"
                            : "bg-warning text-dark"
                        }`}
                      >
                        {task.status}
                      </span>
                    </td>
                    <td>{task.timeframe || "N/A"}</td>
                    <td>{new Date(task.created_at).toLocaleDateString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No tasks found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
